import { useState, useEffect, useCallback } from 'react'
import { useFeatureFlag } from '@fs/zion-flags'

const FOOTER_EXPANSION_KEY = 'fs:footer:expanded'
const hasWindow = typeof window !== 'undefined'
const hasLocalStorage = typeof localStorage !== 'undefined' && localStorage !== null

/**
 * Save a value to localStorage with error handling
 * @param {string} key - The localStorage key
 * @param {any} value - The value to save
 */
const saveToLocalStorage = (key, value) => {
  if (!hasWindow || !hasLocalStorage) return

  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error(`Failed to save ${key} to localStorage:`, error)
  }
}

/**
 * Get a validated boolean value from localStorage
 * @param {string} key - The localStorage key
 * @returns {boolean | null} - The parsed boolean value, or null if not found/invalid
 */
const getValidatedBoolean = (key) => {
  if (!hasWindow || !hasLocalStorage) return null

  try {
    const stored = localStorage.getItem(key)
    if (stored === null) return null

    const parsed = JSON.parse(stored)
    // Only accept actual boolean values
    if (typeof parsed !== 'boolean') {
      // eslint-disable-next-line no-console -- Need to warn developers about corrupted localStorage data
      console.warn(`Invalid value in localStorage for ${key}:`, parsed)
      localStorage.removeItem(key) // Clear corrupted value
      return null
    }
    return parsed
  } catch (error) {
    console.error(`Failed to parse ${key} from localStorage:`, error)
    // Clear corrupted value - ignore errors during cleanup
    try {
      localStorage.removeItem(key)
    } catch {
      // Silently fail if we can't clear the corrupted value
    }
    return null
  }
}

/**
 * Hook to manage footer expansion state with localStorage persistence
 * @param {boolean} showArabicExperience - Whether this is a non-member Arabic experience (cannot expand)
 * @param {boolean} defaultExpanded - Controls default expansion state when no localStorage value exists. Default: false (collapsed).
 * @returns {[boolean, (value: boolean) => void]} - [isExpanded, setIsExpanded]
 */
export default function useFooterExpansion(showArabicExperience = false, defaultExpanded = false) {
  const { isOn: respectDefaultExpanded } = useFeatureFlag('home_footer_respectDefaultExpanded')

  // When flag is OFF, use old behavior (always expanded by default)
  // When flag is ON, respect the defaultExpanded prop
  const effectiveDefault = respectDefaultExpanded ? defaultExpanded : true

  const [isExpanded, setIsExpanded] = useState(() => {
    // For non-member Arabic users, always collapsed - they cannot expand
    if (showArabicExperience) return false

    // Check localStorage for user preference
    const stored = getValidatedBoolean(FOOTER_EXPANSION_KEY)
    return stored ?? effectiveDefault
  })

  // Listen to localStorage changes from other tabs/windows
  useEffect(() => {
    if (!hasWindow || !hasLocalStorage) return undefined

    function handleStorageChange(event) {
      if (event.storageArea === localStorage && event.key === FOOTER_EXPANSION_KEY) {
        // Non-member Arabic users cannot expand, so ignore storage changes
        if (showArabicExperience) return

        const stored = getValidatedBoolean(FOOTER_EXPANSION_KEY)
        setIsExpanded(stored ?? effectiveDefault)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [showArabicExperience, effectiveDefault])

  // Update isExpanded when defaultExpanded changes, but only if no user preference exists
  useEffect(() => {
    if (showArabicExperience) {
      setIsExpanded(false)
      return
    }

    // Only apply the new default if user hasn't made a valid choice
    const stored = getValidatedBoolean(FOOTER_EXPANSION_KEY)
    if (stored === null) {
      setIsExpanded(effectiveDefault)
    }
  }, [effectiveDefault, showArabicExperience])

  const setIsExpandedWrapper = useCallback(
    (value) => {
      if (showArabicExperience && value === true) {
        return
      }

      setIsExpanded(value)

      // Always save to localStorage when user interacts (unless Arabic experience)
      if (!showArabicExperience) {
        saveToLocalStorage(FOOTER_EXPANSION_KEY, value)
      }
    },
    [showArabicExperience]
  )

  return [isExpanded, setIsExpandedWrapper]
}
